import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Button } from 'react-bootstrap'
import Img from 'react-image'
import ReactHtmlParser from 'react-html-parser'
import ReactPlayer from 'react-player'

import { ReactComponent as PanelSwitchOpen } from '../../assets/icons/panel-switch-open.svg'
import { ReactComponent as PanelSwitchClose } from '../../assets/icons/panel-switch-close.svg'
import './LearningPanel.scss'

import { useLearningLab, LearningLabActionType } from '../../contexts/LearningLabContext'
import { useModuleContext } from '../../contexts/ModuleContext'
import { moduleContent } from '../../Module.config'

import * as LearningLab from './LearningLab'
import * as LMS from '../../LMS'
import * as xAPI from '../../api/api'

import { preventContextMenu, isEmpty } from '../../Utils'
import { LessonPanel, SceneRoom, Availability } from '../../Types'

import PanelQuiz from './PanelQuiz'
import { isDebug } from '../../App.config'

type Props = {
    navigateToRoom: Function
}

const LearningPanel: React.FC<Props> = ({ navigateToRoom }: Props) => {
    const context = useModuleContext()
    const { state, dispatch } = useLearningLab()
    const history = useHistory()

    const [panels, setPanels] = useState([] as LessonPanel[])
    const [showPanel, setShowPanel] = useState(true)
    const [showPlayer, setShowPlayer] = useState(false)
    const [showImage, setShowImage] = useState(false)
    const [currentPanelId, setCurrentPanelId] = useState(1)
    const [quizComplete, setQuizComplete] = useState(false)

    const changeLesson = useCallback((lessonID: number) => dispatch({ type: LearningLabActionType.SET_CURRENT_LESSON_ID, payload: lessonID }), [dispatch])

    const handlingLastPanel = useRef(false)

    const changeSection = useCallback((sectionID: number) => {
        dispatch({ type: LearningLabActionType.SET_CURRENT_SECTION_ID, payload: sectionID })
        changeLesson(1)
    }, [changeLesson, dispatch])

    const setCurrentPanel = (panelID: number) => {
        handlingLastPanel.current = false
        setCurrentPanelId(panelID)
        setQuizComplete(false)
    }

    const handleNextLesson = () => {
        changeLesson(state.currentLessonId + 1)
        setCurrentPanel(1)
    }

    const handleNextSection = () => {
        changeSection(state.currentSectionId + 1)
        setCurrentPanel(1)
        navigateToRoom(SceneRoom.Hallway)
    }

    const handlePanelSwitch = (e: React.MouseEvent) => {
        e && e.preventDefault()
        setShowPanel(prevState => !prevState)
        setShowPlayer(prevState => prevState ? false : prevState)
        setShowImage(prevState => prevState ? false : prevState)
    }

    const toggleVideoPlayer = (e: React.MouseEvent) => {
        e && e.preventDefault()
        setShowPlayer(prevState => !prevState)
    }
    const toggleImageViewer = (e: React.MouseEvent) => {
        e && e.preventDefault()
        setShowImage(prevState => !prevState)
    }

    const setModuleAsComplete = () => {
        isDebug && console.log('Setting module as complete...')
        context.LMSAvailable && LMS.setModulePassed()
        context.userProfile['module-score'] = 100
        xAPI.updateLRSModuleScore(context.user, 100)
    }
    useEffect(() => {
        if (state.currentSectionId === 0) return
        isDebug && console.log('Initializing learning panel...')
        setPanels(LearningLab.lesson(state.currentSectionId, state.currentLessonId).panels)
        setCurrentPanelId(1)
    }, [state.currentLessonId, state.currentSectionId])

    if (!panels.length) return <></>

    const isFirstLesson = state.currentLessonId === 1
    const isLastLesson = LearningLab.section(state.currentSectionId).lessons.length === state.currentLessonId
    const isFirstPanel = currentPanelId === 1
    const isLastPanel = panels.length === currentPanelId
    const isLastSection = moduleContent.sections.length === state.currentSectionId

    const previousLearningPanel = (e: React.MouseEvent) => {
        e && e.preventDefault()
        !isFirstPanel && setCurrentPanel(currentPanelId - 1)

    }
    const nextLearningPanel = (e: React.MouseEvent) => {
        e && e.preventDefault()
        !isLastPanel && setCurrentPanel(currentPanelId + 1)

    }

    if (isFirstPanel) {
        isFirstLesson && LearningLab.setSectionAsStarted(state.currentSectionId, context)
        LearningLab.setLessonAsStarted(state.currentSectionId, state.currentLessonId, context)
    }

    if (isLastPanel && handlingLastPanel.current === false) {

        handlingLastPanel.current = true
        isDebug && console.log('Handling last panel...')
        LearningLab.handleLastPanel(state.currentSectionId, state.currentLessonId, context)
            .then(() => {
                isLastLesson && isLastSection && setModuleAsComplete()
            })
    }


    const panel = LearningLab.panel(state.currentSectionId, state.currentLessonId, currentPanelId)
    const isNextLessonDisabled = !isLastLesson &&
        LearningLab.lrsID(state.currentSectionId, state.currentLessonId + 1) === Availability.Disabled
    const isNextSectionDisabled = isLastLesson && !isLastSection &&
        LearningLab.lrsID(state.currentSectionId + 1, undefined) === Availability.Disabled

    const videoPath = LearningLab.assetsPath + 'videos/' + panel.video!.filename
    const imagePath = LearningLab.assetsPath + 'images/' + panel.image!.filename

    const nextPanelButtonDisabled: boolean =
        isLastPanel
        || showPlayer
        || showImage
        || (!isEmpty(panel.quiz!) && !quizComplete)

    return (
        <>
            <div onContextMenu={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => preventContextMenu(e)} className="learning-area">
                <div className="learning-panel interactive" style={panelStyle(showPanel)}>
                    <div className="learning-content">
                        <h4 className="panel-title">{panel.title}</h4>
                        <div className="panel-text">
                            {ReactHtmlParser(panel.text)}
                        </div>

                        <LessonButton
                            class={showImage ? 'primary' : 'secondary'}
                            text={showImage ? 'Close Image' : 'Show Image'}
                            onButtonClick={toggleImageViewer}
                            disabled={showPlayer}
                            visible={!isEmpty(panel.image!)}
                        />

                        <LessonButton
                            class={showPlayer ? 'primary' : 'secondary'}
                            text={showPlayer ? 'Close Video' : 'Play Video'}
                            onButtonClick={toggleVideoPlayer}
                            disabled={showImage}
                            visible={!isEmpty(panel.video!)}
                        />

                        {
                            !isEmpty(panel.quiz!) && <PanelQuiz quiz={panel.quiz!} setQuizComplete={setQuizComplete} />
                        }

                        <LessonButton
                            class='secondary'
                            text='Leave Lesson'
                            onButtonClick={() => navigateToRoom(SceneRoom.Hallway)}
                            visible={isLastPanel && !isLastLesson}
                        />

                        <LessonButton
                            class='secondary'
                            text='Continue to Next Lesson'
                            onButtonClick={handleNextLesson}
                            visible={false}
                        //visible={isLastPanel && !isLastLesson && !isNextLessonDisabled}
                        />

                        <LessonButton
                            class='secondary'
                            text='Continue to Next Section'
                            onButtonClick={handleNextSection}
                            visible={isLastPanel && isLastLesson && !isLastSection && !isNextSectionDisabled}
                        />

                        <LessonButton
                            class='secondary'
                            text='Review this Section'
                            onButtonClick={() => navigateToRoom(SceneRoom.Hallway)}
                            visible={(isLastPanel && isLastLesson) || (isLastPanel && isNextLessonDisabled)}
                        />

                        <LessonButton
                            class='secondary'
                            text='Go to Home Page'
                            onButtonClick={() => history.push('/')}
                            visible={(isLastPanel && isLastLesson) || (isLastPanel && isNextLessonDisabled)}
                        />

                    </div>
                    <div className="panel-nav">
                        <div className="navbuttons">
                            <Button
                                className="previous-panel"
                                disabled={isFirstPanel || showPlayer || showImage}
                                onClick={previousLearningPanel}
                            />
                            <p>{currentPanelId} / {panels.length}</p>
                            <Button
                                className="next-panel"
                                disabled={nextPanelButtonDisabled}
                                onClick={nextLearningPanel}
                            />
                        </div>
                    </div>
                    <div className="panel-switch" onClick={handlePanelSwitch}>
                        {showPanel ? <PanelSwitchClose /> : <PanelSwitchOpen />}
                    </div>
                </div>

                {showPlayer && (
                    <div
                        onContextMenu={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => preventContextMenu(e)}
                        className='media-area'
                        style={mediaAreaStyle(showPanel)}
                    >
                        <div className='close-button' onClick={toggleVideoPlayer} />
                        <div className='media-area-container' style={playerContainerStyle(showPlayer)}>
                            <div className="interactive player-wrapper">
                                {videoPath !== '' && (
                                    <ReactPlayer
                                        id='vid'
                                        playing={true}
                                        controls={true}
                                        width="100%"
                                        height="100%"
                                        pip={false}
                                        className='react-player'
                                        config={{
                                            file: {
                                                attributes: {
                                                    disablePictureInPicture: true,
                                                    controlsList: 'nodownload'
                                                }
                                            }
                                        }}
                                        url={videoPath}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showImage && (
                    <div onContextMenu={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => preventContextMenu(e)} className='media-area' style={mediaAreaStyle(showPanel)}>
                        <div className='media-area-container' style={imageContainerStyle(showImage)}>
                            <div className='close-button' onClick={toggleImageViewer} />
                            <div className="interactive player-wrapper">
                                {imagePath !== '' && <Img alt='' src={imagePath} className='img-fluid' />}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )

}
const panelStyle = (showPanel: boolean) => {
    return {
        animation: `${showPanel ? 'show' : 'hide'}-learning-panel ease .5s alternate`,
        left: `${showPanel ? '0' : '-350px'}`,
        boxShadow: `${showPanel ? '4px 4px 6px -4px rgba(0, 0, 0, 0.47)' : 'none'}`
    } as React.CSSProperties
}

const mediaAreaStyle = (showPanel: boolean) => {
    return {
        animation: `${!showPanel ? 'shrink' : 'grow'}-video-area ease 0s alternate`,
        width: `${!showPanel ? '73.75%' : '100%'}`,
        left: `${!showPanel ? '-350px' : '0'}`,
    } as React.CSSProperties
}

const playerContainerStyle = (showPlayer: boolean) => {
    return {
        animation: `${showPlayer ? 'show' : 'hide'}-videoplayer ease ${showPlayer ? '.5' : '0'}s alternate`,
        opacity: `${showPlayer ? '1' : '0'}`
    } as React.CSSProperties
}

const imageContainerStyle = (showImage: boolean) => {
    return {
        animation: `${showImage ? 'show' : 'hide'}-videoplayer ease ${showImage ? '.5' : '0'}s alternate`,
        opacity: `${showImage ? '1' : '0'}`
    } as React.CSSProperties
}


type LessonButtonProps = {
    text: string
    onButtonClick: Function
    disabled?: boolean
    visible?: boolean
    class: string
}

const LessonButton: React.FC<LessonButtonProps> = (props: LessonButtonProps) => {
    if (!props.visible) return null

    const handleClick = (e: React.MouseEvent) => {
        e && e.preventDefault()
        props.onButtonClick()
    }

    return (
        <div className='button-bar'>
            <button
                className={'button ' + props.class}
                onClick={handleClick}
                disabled={props.disabled}
            >
                {props.text}
            </button>
        </div>
    )
}

export default LearningPanel